import React, { useState } from 'react'
import { Box, Stack, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'

import { PhotovoltaicSystemType } from 'constants/purchaseFlow'
import { GRAY_900 } from 'constants/styling/theme'
import { BorderBoxWrapper } from 'components/common/BorderBoxWrapper'
import { Label } from 'components/common/Label'
import { NumericFloatField } from 'components/common/MUIFloatNumberField'
import { SectionedBorderBox } from 'components/common/SectionedBorderBox'
import { ToggleButton } from 'components/common/ToggleButton'

import type { EnergyPhotovoltaicView } from '../../energyCertificateOfNeed.codec'
import type { UseEnergyCertificateForm } from '../../useEnergyCertificateForm'
import { BuildingOrientation } from '../BuildingOrientation'
import { PRIMARY_FIELD_WIDTH } from './layout'

/** Yes/No toggle for the PV system. */
const PvToggle: React.FC<{ selected: boolean; label: string; onClick: () => void }> = ({ selected, label, onClick }) => (
  <ToggleButton isSelected={selected} label={label} sx={{ width: '6.4rem', height: '4rem', justifyContent: 'center' }} onClick={onClick} typographyVariant="text-md" />
)

export const PhotovoltaicSection: React.FC<{ initial: { system: PhotovoltaicSystemType; features: EnergyPhotovoltaicView }; form: UseEnergyCertificateForm }> = ({ initial, form }) => {
  const { t } = useTranslation(['purchase_flow'], { keyPrefix: 'energy_certificate_step.energy_heating_page' })
  const [system, setSystem] = useState(initial.system)
  const [features, setFeatures] = useState<EnergyPhotovoltaicView>(initial.features)

  const commit = (nextSystem: PhotovoltaicSystemType, nextFeatures: EnergyPhotovoltaicView) => {
    setSystem(nextSystem)
    setFeatures(nextFeatures)
    form.patchPhotovoltaics(nextSystem, nextFeatures)
  }

  return (
    <SectionedBorderBox title={<Typography variant="text-md" fontWeight={600} color={GRAY_900}>{t('photovoltaic_system.photovoltaic_system_title')}</Typography>}>
      <Stack alignItems="flex-start" gap={1.6}>

        <Label text={t('photovoltaic_system.photovoltaic_system_title')} />

        <Stack gap={0.8} flexDirection="row" flexWrap="wrap">
          {Object.values(PhotovoltaicSystemType).map((type) => (
            <PvToggle key={type} selected={system === type} label={t(`photovoltaic_system.photovoltaic_system_type.${type}`)} onClick={() => commit(type, features)} />
          ))}
        </Stack>

        {system === PhotovoltaicSystemType.YES &&
          <BorderBoxWrapper padding={2} width="100%">
            <Stack width="100%" gap={1.6}>

              <Box width={{ xs: '80%', lg: PRIMARY_FIELD_WIDTH }}>
                <NumericFloatField required inputBoxWidth="100%" suffix="m²" label={t('photovoltaic_system.area_of_the_facility')} value={features.areaOfFacility} onCommit={(v) => commit(system, { ...features, areaOfFacility: v })} />
              </Box>

              <Box width={{ xs: '80%', lg: PRIMARY_FIELD_WIDTH }}>
                <NumericFloatField required inputBoxWidth="100%" suffix="kWp" label={t('photovoltaic_system.peak_power_of_the_system')} value={features.peakPower} onCommit={(v) => commit(system, { ...features, peakPower: v })} />
              </Box>

              <Stack marginTop={0.8} width="fit-content">
                <BuildingOrientation onClick={(value) => commit(system, { ...features, orientationType: value })} type={features.orientationType} />
              </Stack>

            </Stack>
          </BorderBoxWrapper>
        }

      </Stack>
    </SectionedBorderBox>
  )
}
